import _ from 'underscore'

import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { color } from 'styles/colors'

import { DeviceActivity } from 'types/DeviceActivity'

import HealthStatusIcon from 'pages/shared/HealthStatusIcon'
import AlertIconWithCount from 'pages/shared/Alert/IconWithCount'
import AlertsTooltip from 'pages/shared/AlertsTooltip'

import { parseAlerts } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'
import { sortBasedOnSeverity, updateAlertsSeverity } from 'helpers/alerts'
import { hasAlerts } from 'helpers/device'
import GA4 from 'helpers/ga4'

import useServerlessActiveAlerts from 'hooks/useServerlessActiveAlerts'

type Props = {
  type: 'hehm' | 'thermostat'
  customerId: string
  device: DeviceActivity
}

const DeviceActivityRow: React.FC<Props> = ({ device, type, customerId }: Props) => {
  const navigate = useNavigate()

  const { deviceId, model, timeZone } = device

  const deviceHasAlerts = hasAlerts(device)

  const { activeAlerts, areActiveAlertsLoaded } = useServerlessActiveAlerts(deviceId, deviceHasAlerts)
  const historyDetails = updateAlertsSeverity(activeAlerts) ?? []
  const alerts = parseAlerts(historyDetails, timeZone)
  const alertsWithoutNormalSeverity = _.reject(
    alerts,
    alert => alert.severity === 'minor' || alert.severity === 'minor-sda'
  )
  const alertIds = _.map(sortBasedOnSeverity(alertsWithoutNormalSeverity), r => r.id).join('-')

  const sendGA4Event = (gaAction: string, gaLabel?: string) => {
    const ga4Event = {
      category: 'Dashboard Page',
      action: gaAction,
      ...(gaLabel && { label: gaLabel })
    }

    GA4.sendEvent(ga4Event)
  }

  const onClickDeviceLink = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
    sendGA4Event('Click AUID 24hrs', deviceId)
  }

  const onClickAlertIcon = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    sendGA4Event('Click Alert 24hrs')
    navigate(`/customers/${customerId}/systems/${deviceId}/alerts`)
  }

  const onClickHealthIcon = (event: React.MouseEvent) => {
    event.stopPropagation()
    sendGA4Event('Click EHMKT Health 24hrs', device.healthScore === 'critical' ? 'Red' : 'Yellow')
    navigate(`/customers/${customerId}/systems/${deviceId}/alerts`)
  }

  return (
    <S.Left>
      <S.TopLine>
        <NavLink to={`/customers/${customerId}/systems/${deviceId}/dashboard`} onClick={onClickDeviceLink}>
          <S.DeviceId>{deviceId}</S.DeviceId>
        </NavLink>

        {type === 'thermostat' && (
          <>
            <S.AlertInline
              onClick={onClickAlertIcon}
              data-tooltip-id={`${deviceId}-alert-tooltip`}
              data-tooltip-content={alertIds}
            >
              <AlertIconWithCount useTooltip={false} severity="critical" count={device.criticalAlarmCount} />
              <AlertIconWithCount useTooltip={false} severity="critical-sda" count={device.sdaCriticalAlarmCount} />
              <AlertIconWithCount useTooltip={false} severity="major" count={device.majorAlarmCount} />
              <AlertIconWithCount useTooltip={false} severity="major-sda" count={device.sdaMajorAlarmCount} />
            </S.AlertInline>
          </>
        )}

        {type === 'hehm' && (
          <S.HealthStatusIcon
            status={device.healthScore}
            onClick={onClickHealthIcon}
            showToolTip={!deviceHasAlerts}
            tooltipId={`${deviceId}-alert-tooltip`}
            tooltipContent={alertIds}
          />
        )}
      </S.TopLine>

      <AlertsTooltip
        id={`${deviceId}-alert-tooltip`}
        alerts={alertsWithoutNormalSeverity}
        isLoaded={areActiveAlertsLoaded}
        place="right"
      />
      <S.DeviceModel>{model}</S.DeviceModel>
    </S.Left>
  )
}

export default DeviceActivityRow

const S = {
  Row: styled.div`
    display: flex;
    padding: 6px 0;
  `,

  Left: styled.div`
    display: flex;
    gap: 4px;
    flex-direction: column;
  `,

  TopLine: styled.div`
    display: flex;
    align-items: center;
  `,

  DeviceId: styled.span`
    font-weight: 500;
    font-size: 14px;
    color: ${color('primaryText')};
    &:hover{
      color: ${color('dashboardCardHoverBorder')};
    }
  `,

  AlertInline: styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
  `,
  HealthStatusIcon: styled(HealthStatusIcon)`
    position: relative;
    margin-top: 1px;
    cursor: pointer;
  `,
  DeviceModel: styled.span`
    font-size: 12px;
  `
}
